import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { FaPlus, FaUsers, FaArrowLeft } from "react-icons/fa";
import { getSingleCampaign, getSingleCampaignRules, postSingleCampaignRules } from "../../services/Campaigns.ts";
import Tasks from "../../components/Tasks.tsx";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { getTheme } from '../../theme.ts';
import Toast from '../../components/common/Toast.tsx';

interface Campaign {
    id: string;
    title: string;
    brand: string;
    type: string;
    deadline: string;
    banner?: string;
    rules: { dos: string[]; donts: string[] };
    tasks: string[];
    brand_details?: { title: string };
    platforms?: string;
    is_live: boolean;
}

interface Rule {
    id: string;
    dos: string[];
    donts: string[];
}

const dummyRequests = [
    { id: 1, name: "John Doe", email: "john@example.com", status: "Pending" },
    { id: 2, name: "Jane Smith", email: "jane@example.com", status: "Approved" },
    { id: 3, name: "Alice Johnson", email: "alice@example.com", status: "Rejected" }
];

const SingleCampaign = () => {
    const { themeName } = useTheme();
    const currentTheme = getTheme(themeName);

    const navigate = useNavigate();
    const reawdata = localStorage.getItem('user');
    let cred = JSON.parse(reawdata || '{}');
    const token = cred?.access;

    const { id } = useParams<{ id: string }>();
    const [campaign, setCampaing] = useState<Campaign | undefined>(undefined);
    const [rules, setRules] = useState<Rule[]>([]);
    const [tasks, setTasks] = useState<string[]>([]);
    const [newDoRule, setNewDoRule] = useState("");
    const [newDontRule, setNewDontRule] = useState("");
    const [newTask, setNewTask] = useState("");
    const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
    const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
    const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
    const [userType, setUserType] = useState<string | null>(null);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' | 'info' } | null>(null);

    const getCampaing = async () => {
        try {
            const response = await getSingleCampaign(token, id);
            if (response) {
                setCampaing(response.data?.data?.campaign);
                setTasks(response.data?.data?.tasks || []);
                setRules(response.data?.data?.rules || []);
            }
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        getCampaing();
    }, [id]);

    const loadUserType = () => {
        const userData = localStorage.getItem("user");
        if (userData) {
            try {
                const parsedUser = JSON.parse(userData);
                setUserType(parsedUser?.user?.role);
            } catch (error) {
                console.error("Error parsing user data:", error);
                setUserType(null);
            }
        } else {
            setUserType(null);
        }
    };

    useEffect(() => {
        loadUserType();
    }, []);

    if (!campaign) {
        return <div className="flex justify-center items-center mt-6">
            <div className="loader border-t-4 border-primary-600 rounded-full w-12 h-12 animate-spin"></div>
            <p className="ml-4 text-primary-700">Loading...</p>
        </div>;
    }

    const addRule = async () => {
        if (newDoRule && newDontRule) {
            const formData = new FormData();
            // Split rules by new lines and filter out empty lines
            const dosArray = newDoRule.split('\n').filter(line => line.trim() !== '');
            const dontsArray = newDontRule.split('\n').filter(line => line.trim() !== '');

            formData.append('dos', JSON.stringify(dosArray));
            formData.append('donts', JSON.stringify(dontsArray));
            formData.append('campaign', id || '');

            try {
                const response = await postSingleCampaignRules(formData);
                if (response && response.status === 201) {
                    getCampaing(); // Refresh campaign data
                    setToast({ message: "Rule added successfully!", type: "success" });
                }
            } catch (err) {
                console.log(err);
                setToast({ message: "Failed to add rule. Please try again.", type: "error" });
            }
            setNewDoRule("");
            setNewDontRule("");
            setIsRuleModalOpen(false);
        } else {
            setToast({ message: "Please add both Do's and Don'ts", type: "warning" });
        }
    };

    const addTask = () => {
        setTasks([...tasks, newTask]);
        setNewTask("");
        setIsTaskModalOpen(false);
    };

    return (
        <div className="min-h-screen" style={{ backgroundColor: currentTheme.background }}>
            {/* Back Button */}
            <div className="p-4">
                <button
                    onClick={() => navigate('/dashboard/campaings')}
                    className="flex items-center gap-2 bg-white text-primary-600 hover:text-primary-800 hover:bg-primary-50 px-4 py-2 rounded-lg shadow-sm border border-primary-200 transition-all duration-200"
                >
                    <FaArrowLeft className="text-lg" />
                    <span className="font-medium">Back to Campaigns</span>
                </button>
            </div>

            <div className="p-4">
                <h2 className="text-3xl font-bold mb-6" style={{ color: currentTheme.text }}>{campaign.title}</h2>

                {/* Campaign Details */}
                <div
  className="shadow-xl rounded-2xl p-6 mb-8 transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 border"
  style={{
    backgroundColor: currentTheme.card,
    borderColor: currentTheme.border,
  }}
>
  {/* Banner */}
  <div className="relative">
    <img
      src={campaign.banner}
      alt="Campaign Banner"
      className="w-full h-56 object-cover rounded-xl"
    />
    {campaign.is_live && (
      <span className="absolute top-3 left-3 bg-green-500 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-md">
        Active
      </span>
    )}
  </div>

  {/* Header Section */}
  <div className="mt-5 flex flex-col sm:flex-row justify-between items-start sm:items-center">
    <h2
      className="text-2xl font-semibold mb-2 sm:mb-0"
      style={{ color: currentTheme.text }}
    >
      {campaign.title || "Untitled Campaign"}
    </h2>

    {userType !== "ADMIN" && (
      <Link
        to={`/dashboard/campaigns/requests/${id}`}
        className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center gap-2 transition-all duration-200 shadow-sm"
      >
        <FaUsers /> Show Requests
      </Link>
    )}
  </div>

  {/* Details Section */}
  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-base">
    <p style={{ color: currentTheme.text }}>
      <strong className="text-gray-500 dark:text-gray-400">Brand:</strong>{" "}
      {campaign.brand_details?.title || "N/A"}
    </p>
    <p style={{ color: currentTheme.text }}>
      <strong className="text-gray-500 dark:text-gray-400">Type:</strong>{" "}
      {campaign.type}
    </p>
    <p style={{ color: currentTheme.text }}>
      <strong className="text-gray-500 dark:text-gray-400">Platforms:</strong>{" "}
      {campaign.platforms
        ? JSON.parse(campaign.platforms).join(", ")
        : "N/A"}
    </p>
    <p style={{ color: currentTheme.text }}>
      <strong className="text-gray-500 dark:text-gray-400">Deadline:</strong>{" "}
      {new Date(campaign.deadline).toLocaleDateString()}
    </p>
  </div>

  {/* Footer */}
  <div className="mt-6 flex justify-end">
    <span
      className={`px-4 py-2 text-sm font-medium rounded-full ${
        campaign.is_live
          ? "bg-green-100 text-green-700"
          : "bg-red-100 text-red-700"
      }`}
    >
      {campaign.is_live ? "Active" : "Inactive"}
    </span>
  </div>
</div>


                {/* Rules Section */}
                <div className="mt-6 shadow-lg rounded-lg p-6 mb-6" style={{ backgroundColor: currentTheme.card, borderColor: currentTheme.border }}>
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-semibold" style={{ color: currentTheme.primary }}>Rules</h3>
                        {userType === "ADMIN" && (
                            <button
                                onClick={() => setIsRuleModalOpen(true)}
                                className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700 flex items-center gap-2"
                            >
                                <FaPlus /> Add Rule
                            </button>
                        )}
                    </div>

                    {/* Do's Section */}
                    <div className="mb-4">
                        <h4 className="font-semibold" style={{ color: currentTheme.primary }}>Do's:</h4>
                        {rules.length > 0 && rules[0].dos.length > 0 ? (
                            <ul className="list-disc list-inside" style={{ color: currentTheme.text }}>
                                {rules[0].dos.map((item, index) => (
                                    <li key={index} className="p-2 border-b">{item}</li>
                                ))}
                            </ul>
                        ) : (
                            <p style={{ color: currentTheme.text }}>No do's added yet.</p>
                        )}
                    </div>

                    {/* Don'ts Section */}
                    <div>
                        <h4 className="font-semibold" style={{ color: currentTheme.primary }}>Don'ts:</h4>
                        {rules.length > 0 && rules[0].donts.length > 0 ? (
                            <ul className="list-disc list-inside" style={{ color: currentTheme.text }}>
                                {rules[0].donts.map((item, index) => (
                                    <li key={index} className="p-2 border-b">{item}</li>
                                ))}
                            </ul>
                        ) : (
                            <p style={{ color: currentTheme.text }}>No don'ts added yet.</p>
                        )}
                    </div>
                </div>

                <Tasks id={id || ''} />
            </div>

            {/* Request Modal */}
            {isRequestModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="p-6 rounded-lg shadow-lg w-96 max-w-full" style={{ backgroundColor: currentTheme.surface, borderColor: currentTheme.border, borderWidth: 1, borderStyle: 'solid' }}>
                        <h3 className="text-lg font-bold mb-4" style={{ color: currentTheme.text }}>User Requests</h3>
                        {dummyRequests.length > 0 ? (
                            <ul style={{ color: currentTheme.text }}>
                                {dummyRequests.map((request) => (
                                    <li key={request.id} className="p-2" style={{ borderBottomColor: currentTheme.border, borderBottomWidth: 1, borderBottomStyle: 'solid' }}>
                                        <p style={{ color: currentTheme.text }}><strong style={{ color: currentTheme.textSecondary }}>Name:</strong> {request.name}</p>
                                        <p style={{ color: currentTheme.text }}><strong style={{ color: currentTheme.textSecondary }}>Email:</strong> {request.email}</p>
                                        <p style={{ color: currentTheme.text }}><strong style={{ color: currentTheme.textSecondary }}>Status:</strong>
                                            <span className="ml-2 px-2 py-1 rounded" style={{
                                                backgroundColor: request.status === "Approved" ? currentTheme.success : request.status === "Pending" ? currentTheme.warning : currentTheme.error,
                                                color: currentTheme.inverseText
                                            }}>
                                                {request.status}
                                            </span>
                                        </p>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p style={{ color: currentTheme.text }}>No requests found.</p>
                        )}
                        <div className="flex justify-end mt-4">
                            <button
                                onClick={() => setIsRequestModalOpen(false)}
                                className="px-4 py-2 rounded"
                                style={{
                                    backgroundColor: currentTheme.secondary,
                                    color: currentTheme.inverseText
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = currentTheme.secondary + 'CC'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = currentTheme.secondary}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Rule Modal */}
            {isRuleModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
                    <div className="p-6 rounded-lg shadow-lg w-96 max-w-full border-2 border-primary-400" style={{ backgroundColor: currentTheme.card }}>
                        <h3 className="text-lg font-bold mb-4" style={{ color: currentTheme.primary }}>Add New Rule</h3>
                        <textarea
                            placeholder="Enter Do Rules (one per line)"
                            value={newDoRule}
                            onChange={(e) => setNewDoRule(e.target.value)}
                            className="border p-2 rounded w-full mb-3 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                            rows={3}
                        />
                        <textarea
                            placeholder="Enter Don't Rules (one per line)"
                            value={newDontRule}
                            onChange={(e) => setNewDontRule(e.target.value)}
                            className="border p-2 rounded w-full mb-4 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                            rows={3}
                        />
                        <div className="flex justify-end space-x-2">
                            <button
                                onClick={() => {
                                    setIsRuleModalOpen(false);
                                    setNewDoRule("");
                                    setNewDontRule("");
                                }}
                                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={addRule}
                                className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700"
                            >
                                Add
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Task Modal */}
            {isTaskModalOpen && (
                <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
                    <div className="p-6 rounded-lg shadow-lg w-96 max-w-full border-2 border-primary-400" style={{ backgroundColor: currentTheme.card }}>
                        <h3 className="text-lg font-bold mb-4" style={{ color: currentTheme.primary }}>Add New Task</h3>
                        <input
                            type="text"
                            placeholder="Enter Task"
                            value={newTask}
                            onChange={(e) => setNewTask(e.target.value)}
                            className="border p-2 rounded w-full mb-4 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                        />
                        <div className="flex justify-end space-x-2">
                            <button
                                onClick={() => {
                                    setIsTaskModalOpen(false);
                                    setNewTask("");
                                }}
                                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={addTask}
                                className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700"
                            >
                                Add
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast Notification */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    show={true}
                    onClose={() => setToast(null)}
                />
            )}
        </div>
    );
};

export default SingleCampaign;
